import { Empty } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

import useBoolean from 'common/hooks/useBoolean';
import SelectButton from './select-button';
import useTriggerSelect from './useTriggerSelect';

const useStyles = makeStyles(
  theme => ({
    wrapper: {
      position: 'relative',
    },
    dropdown: {
      ...theme.typography.body2,
      position: 'absolute',
      top: 40,
      width: 240,
      padding: '4px 0',
      backgroundColor: theme.colors.gray1,
      boxShadow: theme.shadow.modal,
      zIndex: 10,
    },
    dropdownList: {
      maxHeight: 232,
      margin: 0,
      padding: 0,
      listStyle: 'none',
      overflowY: 'auto',
    },
    dropdownItem: {
      ...theme.flex.mainSpaceBetween,
      ...theme.typography.body2,
      margin: 0,
      padding: '5px 12px',
      listStyle: 'none',
      overflowY: 'auto',

      '&:hover': {
        backgroundColor: theme.colors.gray3,
      },
    },
    dropdownItemSelected: {
      color: theme.colors.mainGreen6,
      backgroundColor: theme.colors.mainGreen1,
    },
    selectedIcon: {
      color: theme.colors.mainGreen6,
    },
  }),
  { name: 'SelectComponent' },
);

const SelectComponent = ({
  btnText,
  filterIcon,
  hasOutlinedIcon,
  noBtnBorder,
  onClick,
  onSelect,
  options = [],
  showSelectedOption,
  value,
  isSelected = true,
}) => {
  const wrapperRef = useRef(null);
  const { t } = useTranslation();
  const classes = useStyles();

  const [open, { setFalse: closeModal, toggle: toggleModal }] = useBoolean(false);

  const handleClickOutside = () => {
    if (open) {
      closeModal();
    }
  };

  useTriggerSelect(wrapperRef, () => handleClickOutside(), ['click']);

  const handleSelect = selectValue => {
    onSelect(selectValue);
    closeModal();
  };

  const handleBtnClick = () => {
    onClick?.();
    toggleModal();
  };

  return (
    <div ref={wrapperRef} className={classes.wrapper}>
      <SelectButton
        onClick={handleBtnClick}
        noBtnBorder={noBtnBorder}
        btnText={btnText}
        filterIcon={filterIcon}
        selectedOption={options.find(option => option.value === value)}
        showSelectedOption={showSelectedOption}
        hasOutlinedIcon={hasOutlinedIcon}
        isSelected={isSelected}
      />
      <If condition={open}>
        <div className={classes.dropdown}>
          <Choose>
            <When condition={options.length < 1}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no_data')} />
            </When>
            <Otherwise>
              <ul className={classes.dropdownList}>
                <For of={options} each="option" index="index">
                  <li
                    className={classNames(classes.dropdownItem, {
                      [classes.dropdownItemSelected]: option.value === value,
                    })}
                    key={option.value}
                    onClick={() => {
                      if (option.value !== value) {
                        handleSelect(option.value);
                      }
                    }}
                  >
                    {option.label}
                    <If condition={option.value === value}>
                      <CheckOutlined className={classes.selectedIcon} />
                    </If>
                  </li>
                </For>
              </ul>
            </Otherwise>
          </Choose>
        </div>
      </If>
    </div>
  );
};

SelectComponent.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  onClick: PropTypes.func,
  noBtnBorder: PropTypes.bool,
  value: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  hasOutlinedIcon: PropTypes.bool,
  btnText: PropTypes.string,
  filterIcon: PropTypes.object,
  showSelectedOption: PropTypes.bool,
  isSelected: PropTypes.bool,
};

export default SelectComponent;
